import clsx from "clsx"
import { forwardRef } from "react"

import debounce from "@utils/debounce"
import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Icon } from "@atoms"

import styles from "./Button.module.scss"
import { ButtonProps } from "./Button.types"

function Button(props: ButtonProps, ref: React.Ref<HTMLButtonElement>) {
    const {
        children,
        size,
        variant,
        type = "button",
        className,

        isTabbable = true,
        isFullWidth = false,

        leftIcon,
        leftIconClassName,
        rightIcon,
        rightIconClassName,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isProcessing = false,
        isDisabled = false,
        isLoading = false,

        ...rest
    } = props

    const iconSize = 14

    return (
        <button
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isLoading]: isLoading,
                    [styles.isProcessing]: isProcessing,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                    [styles.iconOnly]:
                        !children && !isProcessing && (leftIcon || rightIcon) && !(leftIcon && rightIcon),
                    [styles.fullWidth]: isFullWidth,
                },
                styles[`${variant}Variant`],
                styles[`${size}Size`],
            )}
            type={type}
            onClick={
                noDebounce
                    ? onClick
                    : debounce((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClick?.(event))
            }
            disabled={isDisabled || isProcessing || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isProcessing}
            aria-pressed={isPressed}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            ref={ref}
            {...rest}
        >
            {leftIcon && (
                <div
                    className={clsx({
                        [styles.iconHidden]: isProcessing,
                    })}
                >
                    {isLoading ? (
                        <div className={styles.iconSkeleton}>
                            <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                        </div>
                    ) : (
                        <Icon container="fixedHeight" icon={leftIcon} className={leftIconClassName} size={iconSize} />
                    )}
                </div>
            )}
            {children && !isProcessing && (
                <span
                    className={clsx({
                        [styles.childrenHidden]: isProcessing,
                    })}
                >
                    {isLoading ? (
                        <div className={styles.contentSkeleton}>
                            <Skeleton height={10} width={40} borderRadius={0} enableAnimation={true} />
                        </div>
                    ) : (
                        children
                    )}
                </span>
            )}
            {isProcessing && (
                <div className={clsx(styles.spinnerWrapper)}>
                    <Icon
                        icon={
                            variant === "solid"
                                ? faIcon({ name: "spinner-third", style: "solid" })
                                : faIcon({ name: "spinner-third", style: "regular" })
                        }
                        spin={true}
                        size={iconSize}
                    />
                </div>
            )}
            {rightIcon && (
                <div
                    className={clsx({
                        [styles.iconHidden]: isProcessing,
                    })}
                >
                    {isLoading ? (
                        <div className={styles.iconSkeleton}>
                            <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                        </div>
                    ) : (
                        <Icon
                            container="fixedHeight"
                            icon={rightIcon}
                            className={rightIconClassName}
                            size={iconSize}
                        />
                    )}
                </div>
            )}
        </button>
    )
}

export default forwardRef(Button)
